import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column, ColumnBodyOptions, ColumnFilterElementTemplateOptions } from "primereact/column";
import { Alert, Severity } from "@energy-advice/alertfan-api-client";
import { Tooltip } from "react-tooltip";
import { addToDate, formatTime, primereactDataTableSortDateFunction } from "utils/time-utils";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { HistoricAlertDTO } from "@energy-advice/easas-api";
import { SelectButton } from "primereact/selectbutton";
import { T } from "services/lang/LanguageService";
import { useGetAlertFan, useGetAlertsHistory } from "api/api-hooks/alert-fan-hooks";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { InputSwitch } from "primereact/inputswitch";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { stringArrayToTags } from "../../basic/strings-array-to-tags/StringArrayToTags";
import ParametersTableBody from "./ParametersTableBody";
import TagsHistory from "./TagsHistory";
import "./AlertFan.scss";
import { LagLeadLoadingConfig } from "../charts/interfaces";
import { defaultFromDateTime, defaultToDateTime } from "../charts/utils";

export enum HiddenColumns {
  TOPIC_TAGS = "Topic tags",
  ITEM_TAGS = "Item tags",
}

type AlertsViewProps = {
  itemsPerPage?: number;
  updateFrequency?: number;
  topicTags?: Array<string>;
  itemTags?: Array<string>;
  loadConf?: LagLeadLoadingConfig;
  hiddenColumns?: string[];

  setTags: (tags: { topics?: Array<string>; items?: Array<string> }) => void;
  setTab: (n: number) => void;
};

const SeverityMap: Record<Severity, any> = {
  SEVERE: (
    <i
      data-tooltip-content={T("word.alert-fan.severe")}
      style={{ color: "red" }}
      className="pi pi-exclamation-triangle severity-body-tooltip"
    />
  ),
  INFO: (
    <i
      data-tooltip-content={T("word.info")}
      style={{ color: "#3B82F6" }}
      className="pi pi-info-circle severity-body-tooltip"
    />
  ),
  DEBUG: (
    <i
      data-tooltip-content={T("action.debug")}
      className="fa fa-solid fa-bug severity-body-tooltip"
    />
  ),
  WARNING: (
    <i
      data-tooltip-content={T("alert.warning")}
      style={{ color: "orange" }}
      className="pi pi-exclamation-triangle severity-body-tooltip"
    />
  ),
};

const AlertsView = ({
  updateFrequency,
  itemsPerPage,
  setTags,
  setTab,
  topicTags,
  itemTags,
  loadConf,
  hiddenColumns,
}: AlertsViewProps) => {
  const { data, refetch, isLoading, error } = useGetAlertFan(new Date().toISOString(), {
    refetchInterval: updateFrequency ? updateFrequency * 1000 : undefined,
    gcTime: 0,
  });
  const [changedTimeOffset, setChangedTimeOffset] = useState<boolean>(false);
  const [selectedTimeOffset, setSelectedTimeOffset] = useState<string | undefined>(undefined);
  const offsetOptions = [
    { label: "H/2", value: "HALFHOUR" },
    { label: "H", value: "HOURS" },
    { label: "D", value: "DAYS" },
    { label: "W", value: "WEEKS" },
    { label: "M", value: "MONTHS" },
  ];
  const currentHiddenColumns = hiddenColumns || [];

  const [tags, setTagsState] = useState(itemTags && topicTags ? [...itemTags, ...topicTags] : []);

  const [dateFrom, setDateFrom] = useState(
    defaultFromDateTime({
      loadConf: {
        alignUnit: "MINUTES",
        lag: 6,
        lagUnit: "HOURS",
        lead: 0,
        leadUnit: "HOURS",
        ...loadConf,
      },
    })
  );

  const [dateTo, setDateTo] = useState(
    defaultToDateTime({
      loadConf: {
        alignUnit: "MINUTES",
        lag: 6,
        lagUnit: "HOURS",
        lead: 0,
        leadUnit: "HOURS",
        ...loadConf,
      },
    })
  );

  const { data: alertsHistory, refetch: refetchHistory } = useGetAlertsHistory({
    containingTags: tags,
    start: dateFrom.toISOString(),
    end: dateTo.toISOString(),
  });

  const [filterableTopicTags, setFilterableTopicTags] = useState<{ tag: string }[]>([]);
  const [filterableItemTags, setFilterableItemTags] = useState<{ tag: string }[]>([]);

  const [topicTagsFilterValue, setTopicTagsFilterValue] = useState<string[]>([]);
  const [itemTagsFilterValue, setItemTagsFilterValue] = useState<string[]>([]);

  const [initialTopicTagsFilter] = useState(topicTags ?? []);
  const [initialItemTagsFilter] = useState(itemTags ?? []);
  const [filteredData, setFilteredData] = useState<(Alert | HistoricAlertDTO)[]>([]);
  const [showHistory, setShowHistory] = useState<boolean>(true);

  const [historyDialog, setHistoryDialog] = useState<boolean>(false);


  const customData = [];

  useEffect(() => {
    let combinedData = [];

    if (showHistory) {
      combinedData = [...(data || []), ...(alertsHistory || [])];
    } else {
      combinedData = data || [];
    }

    const currentFilteredData = combinedData
      .filter(alert => {
        if (initialTopicTagsFilter.length === 0) return true;
        return Array.from(alert.topicTags).some((tag: string) =>
          initialTopicTagsFilter.includes(tag)
        );
      })
      .filter(alert => {
        if (initialItemTagsFilter.length === 0) return true;
        return Array.from(alert.itemTags).some((tag: string) =>
          initialItemTagsFilter.includes(tag)
        );
      });

    const allTopicTags: Set<string> = new Set();
    const allItemTags: Set<string> = new Set();

    combinedData.forEach(obj => {
      obj.topicTags.forEach(tag => allTopicTags.add(tag));
      obj.itemTags.forEach(tag => allItemTags.add(tag));
    });

    const topicTagsTemp = [...allTopicTags].map((tag: string) => ({ tag }));
    const itemTagsTemp = [...allItemTags].map(tag => ({ tag }));

    setFilterableTopicTags(topicTagsTemp);
    setFilterableItemTags(itemTagsTemp);
    setFilteredData(currentFilteredData);
  }, [data, showHistory, alertsHistory, initialTopicTagsFilter, initialItemTagsFilter]);

  useEffect(() => {
    if (selectedTimeOffset) {
      setDateFrom(addToDate(new Date(dateTo), -1, selectedTimeOffset));
    } else if (dateFrom && changedTimeOffset) {
      setDateFrom(
        defaultFromDateTime({
          loadConf,
        })
      );
    }
  }, [selectedTimeOffset]);

  const applyTopicFilters = (value: string[]) => {
    applyFilters("topicTags", value);
  };

  const applyItemFilters = (value: string[]) => {
    applyFilters("itemTags", value);
  };

  const applyFilters = (filterType: string, value: string[]) => {
    const currentFilteredData = customData.filter(alert => {
      if (value.length === 0) return true;

      if (filterType === "topicTags") {
        return alert.topicTags.some((tag: string) => value.includes(tag));
      }
      if (filterType === "itemTags") {
        return alert.itemTags.some((tag: string) => value.includes(tag));
      }

      return true;
    });

    if (filterType === "topicTags") {
      setTopicTagsFilterValue(value);
    } else if (filterType === "itemTags") {
      setItemTagsFilterValue(value);
    }

    setFilteredData(currentFilteredData);
  };

  const rowsPerPageOptions = itemsPerPage
    ? [itemsPerPage, 10, 20, 30]
        .sort((a, b) => a - b)
        .filter(function (x, i, a) {
          return a.indexOf(x) === i;
        })
    : [10, 20, 30];

  const severityBody = (rowData: Alert) => {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Tooltip anchorSelect=".severity-body-tooltip" delayShow={200} />
        {rowData.severity ? SeverityMap[rowData.severity] : null}
      </div>
    );
  };

  const severityFilter = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <div className="flex flex-column gap-2">
        {Object.values(Severity).map(severity => {
          return (
            <div key={severity} className="flex gap-1 align-items-center">
              <Checkbox
                inputId={severity}
                name="category"
                value={severity}
                onChange={e => options.filterCallback(e.value)}
                checked={options.value === severity}
              />
              {SeverityMap[severity]}
              <label htmlFor={severity}>{severity}</label>
            </div>
          );
        })}
      </div>
    );
  };

  const textBody = (rowData: Alert) => {
    return <div dangerouslySetInnerHTML={{ __html: rowData.text }} />;
  };

  const historyAlertBody = (rowData: Alert) => {
    return (
      <>
        <Button
          className="p-button-sm"
          icon="pi pi-history"
          onClick={() => setHistoryDialog(true)}
          tooltip={T("word.alert-history")}
        />
        {historyDialog && (
          <TagsHistory
            tags={[...rowData.itemTags, ...rowData.topicTags]}
            dialog={historyDialog}
            showDialog={setHistoryDialog}
          />
        )}
      </>
    );
  };

  if (error) {
    return (
      <div style={{ border: "1px solid red" }}>
        {T("error.alert-fan.error-while-fetching-alerts")}
      </div>
    );
  }

  const customTagsFilter = (rowData: string[], filterValue: string) => {
    if (filterValue !== null && filterValue.length > 0) {
      return (
        Array.isArray(filterValue) &&
        Array.isArray(rowData) &&
        rowData.some(rowItem => filterValue.includes(rowItem))
      );
    }
    return true;
  };

  const tagItemTemplate = (option: { tag: string }) => {
    let truncatedString = option.tag;
    if (truncatedString.length > 12) {
      truncatedString = `${option.tag.substring(0, 25)}...`;
    }
    return (
      <div key={option.tag}>
        <Tooltip target=".tag-tooltip" />
        <Tag value={truncatedString} className="tag-tooltip" data-pr-tooltip={option.tag} rounded />
      </div>
    );
  };

  const topicTagsFilterChangeHandler = (e, options) => {
    options.filterCallback(e.value);
    applyTopicFilters(e.value);
  };

  const itemTagsFilterChangeHandler = (e, options) => {
    options.filterCallback(e.value);
    applyItemFilters(e.value);
  };

  const topicTagsRowFilterTemplate = (options: ColumnBodyOptions) => {
    return (
      <MultiSelect
        value={topicTagsFilterValue}
        options={filterableTopicTags}
        onChange={e => topicTagsFilterChangeHandler(e, options)}
        optionLabel="tag"
        optionValue="tag"
        placeholder="Tag"
        emptyFilterMessage={() => <div>{T("message.alert-fan.no-tags-to-show")}</div>}
        filter
        itemTemplate={tagItemTemplate}
        maxSelectedLabels={0}
      />
    );
  };

  const itemTagsRowFilterTemplate = options => {
    return (
      <MultiSelect
        value={itemTagsFilterValue}
        options={filterableItemTags}
        onChange={e => itemTagsFilterChangeHandler(e, options)}
        optionLabel="tag"
        optionValue="tag"
        placeholder="Tag"
        emptyFilterMessage={() => <div>{T("message.alert-fan.no-tags-to-show")}</div>}
        filter
        itemTemplate={tagItemTemplate}
        maxSelectedLabels={0}
      />
    );
  };

  const CustomButton = ({ rowData, setTab, setTags }) => {
    <Button
      icon="pi pi-plus"
      className="p-button-sm"
      onClick={() => {
        setTab(1);
        setTags({
          items: rowData.itemTags,
          topics: rowData.topicTags,
        });
      }}
      tooltip={T("action.create-silence")}
    />;
  };

  const onSelectButtonChange = (e: any) => {
    setSelectedTimeOffset(e.value);
    setChangedTimeOffset(true);
  };

  return (
    <div className="flex flex-column gap-2">
      <div className="flex flex-row-reverse  justify-content-between">
        <div className="flex flex-row gap-3 align-self-end">
          <div className="flex flex-column gap-1">
            <label htmlFor="showHistory">{T("action.show-history")}</label>
            <InputSwitch
              checked={showHistory}
              onChange={e => {
                setShowHistory(e.value);
              }}
            />
          </div>
          <Button
            onClick={() => (showHistory ? refetchHistory() : refetch())}
            icon="pi pi-history"
            className="p-button-sm"
          />
        </div>
        {showHistory && (
          <div className="flex flex-row gap-3 align-items-start">
            <CalendarWrapper
              value={dateFrom}
              onChange={e => {
                setDateFrom(e);
                setChangedTimeOffset(false);
                setSelectedTimeOffset(undefined);
              }}
              dateFormat="yy-mm-dd"
            />
            <CalendarWrapper
              value={dateTo}
              onChange={e => {
                setDateTo(e);
                setChangedTimeOffset(false);
                setSelectedTimeOffset(undefined);
              }}
              dateFormat="yy-mm-dd"
            />
            <div style={{ flexWrap: "nowrap", whiteSpace: "nowrap" }}>
              <SelectButton
                value={selectedTimeOffset}
                options={offsetOptions}
                onChange={onSelectButtonChange}
                className="select-button-class"
              />
            </div>
          </div>
        )}
      </div>
      <DataTable
        value={filteredData}
        loading={isLoading}
        paginator={filteredData ? filteredData.length > 10 : false}
        responsiveLayout="scroll"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate={`${T("word.showing")} {first} to {last} of {totalRecords}`}
        rows={itemsPerPage || 10}
        rowsPerPageOptions={rowsPerPageOptions}
        removableSort
        size="small"
      >
        <Column
          header=""
          field="severity"
          style={{ width: 0 }}
          sortable
          filter
          filterMatchMode="contains"
          showFilterMatchModes={false}
          showFilterMenuOptions={false}
          filterElement={severityFilter}
          body={severityBody}
          headerTooltip="Severity"
          headerTooltipOptions={{ position: "top" }}
        />
        <Column
          header={T("word.text")}
          field="text"
          sortable
          filter
          showFilterMatchModes={false}
          showFilterMenuOptions={false}
          filterPlaceholder="Search"
          filterField="text"
          filterMatchMode="contains"
          body={textBody}
        />
        {currentHiddenColumns.find((column: string) => column === HiddenColumns.TOPIC_TAGS) ===
          undefined && (
          <Column
            header={T("word.topic-tags")}
            headerClassName={topicTags && topicTags.length > 0 ? "filtered" : ""}
            field="topicTags"
            body={(e: Alert) => stringArrayToTags(e.topicTags)}
            filter
            filterMatchMode="custom"
            filterElement={topicTagsRowFilterTemplate}
            filterFunction={customTagsFilter}
            showFilterMatchModes={false}
            showFilterMenuOptions={false}
            filterPlaceholder="Tag name"
          />
        )}
        {currentHiddenColumns.find((column: string) => column === HiddenColumns.ITEM_TAGS) ===
          undefined && (
          <Column
            header={T("word.item-tags")}
            headerClassName={itemTags && itemTags.length > 0 ? "filtered" : ""}
            field="itemTags"
            body={(e: Alert) => stringArrayToTags(e.itemTags)}
            filter
            filterMatchMode="custom"
            filterElement={itemTagsRowFilterTemplate}
            filterFunction={customTagsFilter}
            showFilterMatchModes={false}
            showFilterMenuOptions={false}
            filterPlaceholder="Tag name"
          />
        )}
        {showHistory ? (
          <Column
            header={T("word.started")}
            field="start"
            body={rowData => formatTime(rowData.start)}
            sortable
            sortFunction={primereactDataTableSortDateFunction}
          />
        ) : (
          <Column
            header={T("word.started")}
            field="startsAt"
            body={rowData => formatTime(rowData.startsAt)}
            sortable
            sortFunction={primereactDataTableSortDateFunction}
          />
        )}
        {showHistory ? (
          <Column
            header={T("word.ended")}
            field="end"
            body={rowData => formatTime(rowData.end)}
            sortable
            sortFunction={primereactDataTableSortDateFunction}
          />
        ) : (
          <Column
            header={T("word.ended")}
            field="endsAt"
            body={rowData => formatTime(rowData.endsAt)}
            sortable
            sortFunction={primereactDataTableSortDateFunction}
          />
        )}
        <Column
          style={{ width: 0 }}
          body={rowData => <ParametersTableBody properties={rowData.properties} />}
        />
        <Column
          style={{ width: "0" }}
          body={rowData => <CustomButton rowData={rowData} setTab={setTab} setTags={setTags} />}
        />
        <Column body={historyAlertBody} style={{ width: "0" }} />
      </DataTable>
    </div>
  );
};

export default AlertsView;
